// Copyright (c) 2025 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { createContext, useContext, useState, useEffect, useMemo, ReactNode, useCallback } from 'react';
import { getTenant } from '../utils/auth';
import { brandingApi } from '../api/brandingApi';
import type { BrandingConfig } from '../types/branding';
import { DEFAULT_BRANDING } from '../types/branding';

/**
 * Cache configuration
 */
const CACHE_VERSION = '1.0'; // Increment to invalidate all caches
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

interface CachedBranding {
  data: BrandingConfig;
  timestamp: number;
  version: string;
  brokerageId: string;
}

interface BrandingContextValue {
  branding: BrandingConfig;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  clearCache: () => void;
  setBrokerageId: (brokerageId: string) => void;
}

const BrandingContext = createContext<BrandingContextValue | undefined>(undefined);

type BrandingProviderProps = Readonly<{
  children: ReactNode;
  brokerageId?: string; // Optional: can be passed explicitly or loaded from profile
}>;

/**
 * BrandingProvider - Manages branding configuration with localStorage caching
 *
 * Caching Strategy:
 * 1. Load from localStorage immediately (instant UI)
 * 2. Check if cache is valid (not expired, correct version, correct brokerage)
 * 3. If invalid or missing, fetch from API
 * 4. Update cache and UI with fresh data
 * 5. Apply custom colors to CSS variables
 *
 * Cache Key Format: `branding_${brokerageId}_${subdomain}`
 * This ensures no clash if client uses multiple brokerages
 */
export function BrandingProvider({ children, brokerageId: propBrokerageId }: BrandingProviderProps) {
  const [branding, setBranding] = useState<BrandingConfig>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentBrokerageId, setCurrentBrokerageId] = useState<string | null>(propBrokerageId || null);

  /**
   * Get cache key for the current brokerage
   */
  const getCacheKey = (brokerageId: string): string => {
    const subdomain = getTenant();
    return `branding_${brokerageId}_${subdomain}`;
  };

  /**
   * Try to apply a single cache entry. Returns true if it was used (and the
   * caller should stop scanning), false if it was invalid (already deleted by
   * this fn) or unparsable.
   */
  const tryApplyCacheEntry = useCallback((key: string, subdomain: string): boolean => {
    const cached = localStorage.getItem(key);
    if (!cached) return false;
    try {
      const cachedData: CachedBranding = JSON.parse(cached);
      const isExpired = Date.now() - cachedData.timestamp > CACHE_TTL_MS;
      const isWrongVersion = cachedData.version !== CACHE_VERSION;
      if (isExpired || isWrongVersion) {
        localStorage.removeItem(key);
        return false;
      }
      console.log(`[BrandingContext] Found cached branding for tenant ${subdomain}, brokerageId ${cachedData.brokerageId}`);
      setBranding(cachedData.data);
      applyColors(cachedData.data);
      // IMPORTANT: Don't setCurrentBrokerageId here — Layout calls setBrokerageId()
      // later, and triggering the API-calling useEffect from cache load defeats
      // the cache.
      setLoading(false);
      return true;
    } catch (err) {
      console.error('[BrandingContext] Failed to parse cached branding:', err);
      return false;
    }
  }, []);

  /**
   * Load cached branding for current tenant (without knowing brokerageId yet)
   * Scans localStorage for any valid cache entry matching the current subdomain
   *
   * IMPORTANT: This only loads from cache, does NOT trigger API calls
   */
  const loadCachedBrandingForTenant = useCallback((): void => {
    try {
      const subdomain = getTenant();
      const prefix = `branding_`;
      const suffix = `_${subdomain}`;
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (!key?.startsWith(prefix) || !key.endsWith(suffix)) continue;
        if (tryApplyCacheEntry(key, subdomain)) return;
      }
      console.log(`[BrandingContext] No valid cached branding found for tenant ${subdomain}`);
      setLoading(false);
    } catch (err) {
      console.error('[BrandingContext] Error loading cached branding:', err);
      setLoading(false);
    }
  }, [tryApplyCacheEntry]);

  /**
   * Load branding from localStorage cache
   */
  const loadFromCache = (brokerageId: string): BrandingConfig | null => {
    try {
      const cacheKey = getCacheKey(brokerageId);
      const cached = localStorage.getItem(cacheKey);

      if (!cached) {
        return null;
      }

      const cachedData: CachedBranding = JSON.parse(cached);

      // Validate cache
      const isExpired = Date.now() - cachedData.timestamp > CACHE_TTL_MS;
      const isWrongVersion = cachedData.version !== CACHE_VERSION;
      const isWrongBrokerage = cachedData.brokerageId !== brokerageId;

      if (isExpired || isWrongVersion || isWrongBrokerage) {
        // Invalid cache, remove it
        localStorage.removeItem(cacheKey);
        return null;
      }

      console.log(`[BrandingContext] Loaded branding from cache for brokerage ${brokerageId}`);
      return cachedData.data;
    } catch (err) {
      console.error('[BrandingContext] Failed to load from cache:', err);
      return null;
    }
  };

  /**
   * Save branding to localStorage cache
   */
  const saveToCache = (brokerageId: string, brandingData: BrandingConfig): void => {
    try {
      const cacheKey = getCacheKey(brokerageId);
      const cachedData: CachedBranding = {
        data: brandingData,
        timestamp: Date.now(),
        version: CACHE_VERSION,
        brokerageId
      };

      localStorage.setItem(cacheKey, JSON.stringify(cachedData));
      console.log(`[BrandingContext] Saved branding to cache for brokerage ${brokerageId}`);
    } catch (err) {
      console.error('[BrandingContext] Failed to save to cache:', err);
    }
  };

  /**
   * Apply branding colors to CSS variables
   */
  const applyColors = (brandingData: BrandingConfig): void => {
    if (brandingData.colors?.primary) {
      document.documentElement.style.setProperty('--primary-color', brandingData.colors.primary);
    }
    if (brandingData.colors?.secondary) {
      document.documentElement.style.setProperty('--secondary-color', brandingData.colors.secondary);
    }
  };

  /**
   * Fetch branding from API
   */
  const fetchBranding = async (brokerageId: string): Promise<BrandingConfig> => {
    try {
      console.log(`[BrandingContext] Fetching branding from API for brokerage ${brokerageId}`);
      const brandingData = await brandingApi.getBrandingConfig(brokerageId);

      // Save to cache
      saveToCache(brokerageId, brandingData);

      // Apply colors
      applyColors(brandingData);

      return brandingData;
    } catch (err: unknown) {
      // Handle 401 (Unauthorized) specially - user is logged out
      const error = err as { response?: { status?: number } };
      if (error?.response?.status === 401) {
        console.warn('[BrandingContext] Unauthorized (401) - clearing cache and using default branding');
        // Clear cache to prevent retry loop
        const cacheKey = getCacheKey(brokerageId);
        localStorage.removeItem(cacheKey);
        // Don't retry, just return default
        return DEFAULT_BRANDING;
      }

      console.error('[BrandingContext] Failed to fetch branding:', err);
      throw err;
    }
  };

  /**
   * Load branding (from cache or API)
   */
  const loadBranding = useCallback(async (brokerageId: string): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      // Try loading from cache first
      const cachedBranding = loadFromCache(brokerageId);

      if (cachedBranding) {
        // Use cached data immediately
        setBranding(cachedBranding);
        applyColors(cachedBranding);
        setLoading(false);

        // Fetch in background to check for updates (fire and forget)
        fetchBranding(brokerageId)
          .then((freshBranding) => {
            // Only update if data changed
            if (JSON.stringify(freshBranding) !== JSON.stringify(cachedBranding)) {
              console.log('[BrandingContext] Branding updated from API');
              setBranding(freshBranding);
            }
          })
          .catch((err: unknown) => {
            // If 401, use default branding (user logged out)
            const error = err as { response?: { status?: number } };
            if (error?.response?.status === 401) {
              console.warn('[BrandingContext] Background fetch unauthorized - using default branding');
              setBranding(DEFAULT_BRANDING);
              applyColors(DEFAULT_BRANDING);
            } else {
              console.warn('[BrandingContext] Background fetch failed (using cache):', err);
            }
          });
      } else {
        // No cache, fetch from API
        const freshBranding = await fetchBranding(brokerageId);
        setBranding(freshBranding);
        setLoading(false);
      }
    } catch (err: unknown) {
      // If 401, silently fall back to default (user logged out)
      const error = err as { response?: { status?: number } };
      if (error?.response?.status === 401) {
        console.warn('[BrandingContext] Load branding unauthorized - using default branding');
        setBranding(DEFAULT_BRANDING);
        applyColors(DEFAULT_BRANDING);
        setLoading(false);
        return;
      }

      console.error('[BrandingContext] Failed to load branding:', err);
      setError('Failed to load branding configuration');
      setBranding(DEFAULT_BRANDING);
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Refetch branding (bypasses cache)
   */
  const refetch = useCallback(async (): Promise<void> => {
    if (!currentBrokerageId) {
      console.warn('[BrandingContext] Cannot refetch: No brokerage ID');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const freshBranding = await fetchBranding(currentBrokerageId);
      setBranding(freshBranding);
    } catch (err) {
      console.error('[BrandingContext] Refetch failed:', err);
      setError('Failed to refresh branding');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBrokerageId]);

  /**
   * Clear cache for current brokerage
   */
  const clearCache = useCallback((): void => {
    if (!currentBrokerageId) {
      console.warn('[BrandingContext] Cannot clear cache: No brokerage ID');
      return;
    }

    const cacheKey = getCacheKey(currentBrokerageId);
    localStorage.removeItem(cacheKey);
    console.log(`[BrandingContext] Cleared cache for brokerage ${currentBrokerageId}`);
  }, [currentBrokerageId]);

  /**
   * Load cached branding immediately on mount (before brokerageId is known)
   */
  useEffect(() => {
    loadCachedBrandingForTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Load branding when brokerage ID changes
   */
  useEffect(() => {
    if (currentBrokerageId) {
      loadBranding(currentBrokerageId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBrokerageId]);

  const value = useMemo<BrandingContextValue>(() => ({
    branding,
    loading,
    error,
    refetch,
    clearCache,
    setBrokerageId: setCurrentBrokerageId
  }), [branding, loading, error, refetch, clearCache]);

  return <BrandingContext.Provider value={value}>{children}</BrandingContext.Provider>;
}

/**
 * Hook to access branding context
 */
export function useBranding(): BrandingContextValue {
  const context = useContext(BrandingContext);

  if (context === undefined) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }

  return context;
}
